import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Truck, Camera, Navigation, Flag, Play, Images, Loader2, CheckCircle2, ListChecks, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { InstructionBanner, SearchBar, searchMatch, EmptyState } from "@/components/Bits";
import { JobChecklists } from "@/components/JobChecklists";
import { InspectionDialog } from "@/components/fleet/InspectionDialog";
import { JobLeadFlow } from "@/components/crew/JobLeadFlow";
import { MyFlagsStrip, JobHelperActions, StillClockedInNudge } from "@/components/crew/CrewExtras";
import { myJobsApi, myTimeApi, setJobStatusApi, uploadJobPhotoApi, listJobPhotosApi, photoUrl, crewLeadFlowApi, apiErrorMessage } from "@/lib/api";
import { fmtDate, todayISO, mapsLink } from "@/lib/format";

const NEXT_LABEL = { "En Route": "I'm on the way", Arrived: "I've arrived", "In Progress": "Start the job", Complete: "Finish the job" };
const NEXT_ICON = { "En Route": Navigation, Arrived: MapPin, "In Progress": Play, Complete: Flag };
const STATUS_ORDER = ["Assigned", "En Route", "Arrived", "In Progress", "Complete"];
const STATUS_STYLE = {
  Assigned: "bg-surface-sunk text-ink-2 border-border-strong",
  "En Route": "bg-info/12 text-info border-info/30",
  Arrived: "bg-warning/12 text-warning border-warning/30",
  "In Progress": "bg-primary/10 text-primary border-primary/25",
  Complete: "bg-success/12 text-success border-success/30",
};

const DELAY_FACTORS = ["Stairs", "Long carry", "Elevator wait", "Customer not packed", "Heavy/specialty items", "Traffic", "Weather", "Customer added items", "None"];

const PhotoSection = ({ jobId }) => {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadPhotos = useCallback(() => listJobPhotosApi(jobId).then(setPhotos).catch(() => setPhotos([])), [jobId]);

  const toggle = () => {
    if (!open && photos === null) loadPhotos();
    setOpen((o) => !o);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadJobPhotoApi(jobId, file);
      toast.success("Photo saved.");
      await loadPhotos();
      setOpen(true);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
    setUploading(false);
    e.target.value = "";
  };

  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="flex items-center gap-2">
        <Button data-testid="job-photos-toggle-btn" variant="outline" size="sm" className="gap-1.5 text-xs" onClick={toggle}>
          <Images className="w-3.5 h-3.5" /> {open ? "Hide photos" : `Photos${photos ? ` (${photos.length})` : ""}`}
        </Button>
        <label className="inline-flex">
          <input data-testid="job-photo-input" type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
          <span className="inline-flex items-center gap-1.5 cursor-pointer rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink-2 hover:bg-surface-sunk transition-colors">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
            {uploading ? "Uploading…" : "Add photo"}
          </span>
        </label>
      </div>
      {open && photos && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
          {photos.length === 0 && <p className="col-span-full text-xs text-faint">No photos yet — snap the truck, the rooms, or any damage before you start.</p>}
          {photos.map((p) => (
            <a key={p.id} href={photoUrl(p.id)} target="_blank" rel="noreferrer">
              <img data-testid="job-photo-thumb" src={photoUrl(p.id)} alt={`By ${p.by}`} className="w-full h-20 object-cover rounded-md border border-border" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

const ChecklistSection = ({ job, onChanged, clockedIn }) => {
  const [open, setOpen] = useState(false);
  const [inspOpen, setInspOpen] = useState(false);
  return (
    <div className="mt-3 border-t border-border pt-3">
      <div className="flex items-center gap-2">
        <Button data-testid="job-checklists-toggle-btn" variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setOpen((o) => !o)}>
          <ListChecks className="w-3.5 h-3.5" /> {open ? "Hide checklists" : "Checklists"}
        </Button>
        {job.truck_id && (
          <Button data-testid="job-inspection-btn" variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setInspOpen(true)}>
            <ClipboardCheck className="w-3.5 h-3.5" /> Daily inspection
          </Button>
        )}
      </div>
      {open && <JobChecklists assignmentId={job.id} truckId={job.truck_id} truckName={job.truck_name} onStatusAdvance={onChanged} clockedIn={clockedIn} />}
      {job.truck_id && (
        <InspectionDialog open={inspOpen} onOpenChange={setInspOpen} truckId={job.truck_id} truckName={job.truck_name}
          assignmentId={job.id} onDone={onChanged} />
      )}
    </div>
  );
};

const JobCard = ({ job, onChanged, clockedIn }) => {
  const [busy, setBusy] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [factors, setFactors] = useState([]);
  const [leadFlow, setLeadFlow] = useState(undefined);

  useEffect(() => {
    crewLeadFlowApi(job.id).then(setLeadFlow).catch(() => setLeadFlow(null));
  }, [job.id]);
  const flowMode = !!leadFlow && typeof leadFlow === "object"; // Crew Lead flow owns status + checklists
  const legacyMode = leadFlow === null; // flow unavailable → fall back to the old buttons

  const toggleFactor = (fct) =>
    setFactors((cur) => {
      if (cur.includes(fct)) return cur.filter((x) => x !== fct);
      if (fct === "None") return ["None"];
      return [...cur.filter((x) => x !== "None"), fct];
    });
  const idx = STATUS_ORDER.indexOf(job.exec_status || "Assigned");
  const next = STATUS_ORDER[idx + 1];
  const NextIcon = next ? NEXT_ICON[next] : CheckCircle2;

  const advance = async (status, extraNotes, delayFactors) => {
    setBusy(true);
    try {
      await setJobStatusApi(job.id, status, extraNotes, delayFactors);
      toast.success(status === "Complete" ? "Job done — nice work!" : `Marked "${status}".`);
      setCompleteOpen(false);
      onChanged();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setBusy(false);
  };

  return (
    <div data-testid="crew-job-card" className="surface p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-bold text-primary">{job.job_name}</p>
          <p className="text-xs text-faint mt-0.5">
            {fmtDate(job.job_date)}
            {job.arrival_time ? ` · arrive ${job.arrival_time}` : ""}
          </p>
        </div>
        <Badge data-testid="job-exec-status" variant="outline" className={`text-[10px] shrink-0 ${STATUS_STYLE[job.exec_status] || STATUS_STYLE.Assigned}`}>
          {job.exec_status || "Assigned"}
        </Badge>
      </div>
      <div className="mt-2 space-y-1 text-sm">
        {job.start_address && (
          <a data-testid="job-address-link" href={mapsLink(job.start_address)} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-primary underline decoration-dotted underline-offset-2">
            <MapPin className="w-3.5 h-3.5 text-accent-ink shrink-0" /> {job.start_address}
          </a>
        )}
        <p className="flex items-center gap-3 text-xs text-faint">
          {job.truck_name && (
            <span className="inline-flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> {job.truck_name}</span>
          )}
          {job.my_position && <span className="font-semibold text-accent-ink">You're the {job.my_position}</span>}
        </p>
      </div>
      {legacyMode && next && (
        <Button
          data-testid="job-next-status-btn"
          disabled={busy}
          onClick={() => (next === "Complete" ? setCompleteOpen(true) : advance(next))}
          className="w-full mt-3 gap-2 bg-primary hover:bg-[#152238]"
        >
          <NextIcon className="w-4 h-4" /> {NEXT_LABEL[next]}
        </Button>
      )}
      {job.exec_status === "Complete" && job.completion_notes && (
        <p className="text-xs text-faint mt-2 bg-surface-sunk rounded p-2">Notes: {job.completion_notes}</p>
      )}
      {flowMode && <JobLeadFlow assignmentId={job.id} seedFlow={leadFlow} onChanged={onChanged} />}
      {legacyMode && <ChecklistSection job={job} onChanged={onChanged} clockedIn={clockedIn} />}
      {flowMode && <JobHelperActions assignmentId={job.id} leadFlow={leadFlow} />}
      <PhotoSection jobId={job.id} />
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent data-testid="complete-job-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Finish "{job.job_name}"?</DialogTitle>
            <DialogDescription>Add a quick note about how it went (optional). The boss gets pinged to ask the customer for a review.</DialogDescription>
          </DialogHeader>
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-faint mb-2">Anything slow you down?</p>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              {DELAY_FACTORS.map((fct) => (
                <label key={fct} data-testid="delay-factor-option" className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={factors.includes(fct)} onCheckedChange={() => toggleFactor(fct)} />
                  {fct}
                </label>
              ))}
            </div>
          </div>
          <Textarea data-testid="complete-notes-input" placeholder="Anything to flag? Damage, extra stops, great customer…" value={notes} onChange={(e) => setNotes(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)}>Not yet</Button>
            <Button data-testid="complete-confirm-btn" disabled={busy} onClick={() => advance("Complete", notes, factors.length ? factors : ["None"])} className="bg-success hover:bg-success gap-1.5">
              <Flag className="w-4 h-4" /> Mark complete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function CrewJobs() {
  const [jobs, setJobs] = useState(null);
  const [query, setQuery] = useState("");
  const [clockedIn, setClockedIn] = useState(undefined);
  const [nudge, setNudge] = useState(false);

  const load = useCallback(() => {
    myJobsApi().then(setJobs).catch(() => setJobs([]));
    myTimeApi().then((d) => { setClockedIn(!!d.clocked_in); setNudge(!!d.still_clocked_in_after_complete); })
      .catch(() => setClockedIn(undefined));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const today = todayISO();
  const list = (jobs || []).filter((j) => searchMatch(query, j.job_name, j.start_address, j.truck_name, j.exec_status, j.my_position));
  const todays = list.filter((j) => j.job_date === today);
  const upcoming = list.filter((j) => j.job_date > today);
  const past = list.filter((j) => j.job_date < today).reverse();

  const Section = ({ title, items, testId }) => (
    <div data-testid={testId}>
      <h2 className="text-sm font-bold uppercase tracking-wide text-faint mb-2">{title}</h2>
      {items.length === 0 ? (
        <EmptyState>Nothing on the board yet. Jobs land here as soon as dispatch assigns you.</EmptyState>
      ) : (
        <div className="space-y-3">{items.map((j) => <JobCard key={j.id} job={j} onChanged={load} clockedIn={clockedIn} />)}</div>
      )}
    </div>
  );

  return (
    <div data-testid="crew-jobs-page" className="space-y-6 max-w-xl">
      <div>
        <h1 className="text-2xl font-bold text-primary">My Jobs</h1>
        <p className="text-sm text-faint">Your moves for the next two weeks.</p>
      </div>
      <InstructionBanner testId="crew-jobs-banner">
        Tap the big button as your day moves along: on the way → arrived → start → finish. Snap photos before and after.
      </InstructionBanner>
      <SearchBar value={query} onChange={setQuery} placeholder="Search your jobs…" testId="crew-jobs-search-input" />
      <StillClockedInNudge show={nudge} />
      <MyFlagsStrip />
      {jobs === null ? (
        <p className="text-sm text-faint">Loading your jobs…</p>
      ) : (
        <>
          <Section title="Today" items={todays} testId="jobs-today-section" />
          <Section title="Coming up" items={upcoming} testId="jobs-upcoming-section" />
          {past.length > 0 && <Section title="Recent" items={past} testId="jobs-past-section" />}
        </>
      )}
    </div>
  );
}
